import { Color, Rectangle, Cartographic, JulianDate, Cartesian2, EllipsoidGeodesic, Math as CesiumMath } from "cesium";

export const colorPolylinesBasedOnSharedEndpoints = (viewer, enabled) => {
  // --- Early guards ---
  if (!viewer || !viewer.dataSources) return;

  // --- Same thresholds/logic style as the previous function ---
  const DIST_THRESH_M = 0.001; // 1 mm (horizontal only)
  const HEIGHT_TOL_M = 0.01; // 1 cm
  const BUCKET_DEG = 1e-5; // ~1.1 m lat bucket for coarse prune

  // --- Helper: get all polyline entities across all data sources (unchanged behavior) ---
  function getAllPolylines() {
    const out = [];
    // NOTE: Uses private _dataSources just like the original; kept intentionally unchanged.
    viewer.dataSources._dataSources.forEach((ds) => {
      ds.entities.values.forEach((e) => {
        if (e?.polyline?.positions) out.push(e);
      });
    });
    return out;
  }

  // --- Viewport helpers (unchanged) ----------------------------------------

  /** Compute the current view rectangle (radians). Fast path + fallback via corner picking. */
  function getViewRectangle(viewer) {
    const ellipsoid = viewer?.scene?.globe?.ellipsoid;
    if (ellipsoid) {
      const rect = viewer.camera.computeViewRectangle(ellipsoid);
      if (rect) return rect;
    }

    const scene = viewer?.scene;
    const globe = scene?.globe;
    const camera = scene?.camera;
    const canvas = scene?.canvas;
    if (!scene || !globe || !camera || !canvas) return undefined;

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const picks = [new Cartesian2(0, 0), new Cartesian2(w, 0), new Cartesian2(0, h), new Cartesian2(w, h), new Cartesian2(w / 2, h / 2)];

    const carts = [];
    for (const p of picks) {
      const ray = camera.getPickRay(p);
      if (!ray) continue;
      const pos = globe.pick(ray, scene);
      if (pos) carts.push(Cartographic.fromCartesian(pos));
    }
    if (carts.length === 0) return undefined;

    let west = Number.POSITIVE_INFINITY;
    let south = Number.POSITIVE_INFINITY;
    let east = Number.NEGATIVE_INFINITY;
    let north = Number.NEGATIVE_INFINITY;

    for (const c of carts) {
      if (c.longitude < west) west = c.longitude;
      if (c.longitude > east) east = c.longitude;
      if (c.latitude < south) south = c.latitude;
      if (c.latitude > north) north = c.latitude;
    }
    return new Rectangle(west, south, east, north);
  }

  /** True if a Cartesian position falls inside the given view rectangle. */
  function isInViewRect(cartesian, rect) {
    if (!rect) return true;
    const c = Cartographic.fromCartesian(cartesian);
    return Rectangle.contains(rect, c);
  }

  // --- Distance + bucketing helpers (NEW: match previous function semantics) ---

  // surface (geodesic) distance in meters, ignoring height
  function surfaceDistanceMeters(p1, p2) {
    const c1 = Cartographic.fromCartesian(p1);
    const c2 = Cartographic.fromCartesian(p2);
    c1.height = 0;
    c2.height = 0;
    const g = new EllipsoidGeodesic(c1, c2);
    return g.surfaceDistance || 0;
  }

  // coarse 2D bucket from lat/lon (degrees)
  function bucketKeyFromCartesian(pos) {
    const c = Cartographic.fromCartesian(pos);
    const latDeg = CesiumMath.toDegrees(c.latitude);
    const lonDeg = CesiumMath.toDegrees(c.longitude);
    const bx = Math.round(lonDeg / BUCKET_DEG);
    const by = Math.round(latDeg / BUCKET_DEG);
    return `${bx},${by}`;
  }

  // neighbor bucket keys (3x3)
  function neighborBucketKeys(pos) {
    const c = Cartographic.fromCartesian(pos);
    const latDeg = CesiumMath.toDegrees(c.latitude);
    const lonDeg = CesiumMath.toDegrees(c.longitude);
    const bx = Math.round(lonDeg / BUCKET_DEG);
    const by = Math.round(latDeg / BUCKET_DEG);
    const keys = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        keys.push(`${bx + dx},${by + dy}`);
      }
    }
    return keys;
  }

  // --- When toggled OFF: restore original materials (unchanged) ---
  if (!enabled) {
    const polylines = getAllPolylines();
    for (const e of polylines) {
      const orig = e.polyline._originalMaterial;
      if (orig) e.polyline.material = orig;
      delete e._isUsingStartPoint;
      delete e._isUsingEndPoint;
    }
    return;
  }

  // --- Toggled ON: compute and apply colors (viewport-aware) ---
  const polylines = getAllPolylines();
  if (polylines.length === 0) return;

  const now = JulianDate.now();
  const viewRect = getViewRectangle(viewer);

  // Collect visible endpoints (start/end) that are inside viewport
  const endpoints = []; // { position, entity }
  for (const e of polylines) {
    const positions = e.polyline.positions.getValue(now);
    if (!positions || positions.length < 2) continue;

    const start = positions[0];
    const end = positions[positions.length - 1];

    if (isInViewRect(start, viewRect)) endpoints.push({ position: start, entity: e });
    if (isInViewRect(end, viewRect)) endpoints.push({ position: end, entity: e });
  }

  // Bucket endpoints (coarse prune)
  const buckets = new Map(); // bucketKey -> array of endpoint indices
  for (let i = 0; i < endpoints.length; i++) {
    const k = bucketKeyFromCartesian(endpoints[i].position);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(i);
  }

  // "Shared endpoint" test using previous function's rules:
  // - nearby endpoint exists within DIST_THRESH_M (horizontal only)
  // - and |height diff| <= HEIGHT_TOL_M
  function hasNearbyCompatibleEndpoint(pos, ownerEntity) {
    const h1 = Cartographic.fromCartesian(pos).height || 0;

    const nkeys = neighborBucketKeys(pos);
    for (const k of nkeys) {
      const idxs = buckets.get(k);
      if (!idxs) continue;

      for (const j of idxs) {
        const other = endpoints[j];
        if (!other) continue;
        if (other.entity === ownerEntity) continue; // must be another polyline

        const d = surfaceDistanceMeters(pos, other.position);
        if (d >= DIST_THRESH_M) continue;

        const h2 = Cartographic.fromCartesian(other.position).height || 0;
        const hdiff = Math.abs(h1 - h2);
        if (hdiff <= HEIGHT_TOL_M) return true;
      }
    }
    return false;
  }

  for (const e of polylines) {
    const positions = e.polyline.positions.getValue(now);
    if (!positions || positions.length < 2) continue;

    const start = positions[0];
    const end = positions[positions.length - 1];

    const startInView = isInViewRect(start, viewRect);
    const endInView = isInViewRect(end, viewRect);

    // If neither endpoint is in the viewport: restore original material and skip (unchanged behavior)
    if (!startInView && !endInView) {
      if (e.polyline._originalMaterial) {
        e.polyline.material = e.polyline._originalMaterial;
      }
      delete e._isUsingStartPoint;
      delete e._isUsingEndPoint;
      continue;
    }

    const isUsingStartPoint = startInView ? hasNearbyCompatibleEndpoint(start, e) : false;
    const isUsingEndPoint = endInView ? hasNearbyCompatibleEndpoint(end, e) : false;

    if (!e.polyline._originalMaterial) {
      e.polyline._originalMaterial = e.polyline.material;
    }

    // Same color rules as original:
    // - LIME: both endpoints "shared"
    // - RED: neither endpoint "shared"
    // - ORANGE: exactly one endpoint "shared"
    let color = Color.ORANGE;
    if (isUsingStartPoint && isUsingEndPoint) {
      color = Color.LIME;
    } else if (!isUsingStartPoint && !isUsingEndPoint) {
      color = Color.RED;
    }

    e.polyline.material = color;

    e._isUsingStartPoint = isUsingStartPoint;
    e._isUsingEndPoint = isUsingEndPoint;
  }
};
